import { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { LoadingGroup, Panel, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body } from "capitalrx-components-2.0/lib/typography";

import JudiCareLogo from "@/App/components/MemberCard/Assets/judicare-logo.png";
import { ThemedButton } from "@/App/components/themed/Button";
import { ExternalConnectionLink } from "@/App/api/externalConnections";
import { SearchParamKeys } from "@/App/constants/constants";
import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useNavigateClientNewTab } from "@/App/hooks";
import { useListCategorizedExternalConnectionList } from "@/App/hooks/externalConnections";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

const TRANSLATION_PATH = "DASHBOARD.judiCarePanel";

const LogoImg = styled.img`
  height: 32px;
  object-fit: contain;
`;

export const JudiCareTile: FC = () => {
  const { t: translate } = useTranslation();
  const { hideJudiCareOutboundSSO } = useContext(ExperienceContext);
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { externalConnectionsJudiCare, isLoading } = useListCategorizedExternalConnectionList({
    location: window.location.href,
  });
  const navigate = useNavigateClientNewTab();

  const onExternalConnection = useCallback((link: ExternalConnectionLink) => {
    logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_judi_care_external_link", { path: location });
    const urlParams = new URLSearchParams();
    if (isViewingDataholder) {
      urlParams.set(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
    }
    const toValue = `${location.protocol}//${location.host}/connection/${link.id}`;
    navigate(toValue, urlParams);
  }, []);

  const { isMobile } = useResponsive({
    isMobile: { desktop: false, mobile: true },
  });

  if (hideJudiCareOutboundSSO || externalConnectionsJudiCare.length === 0) {
    return null;
  }

  const button = (
    <ThemedButton
      testId={`link-${externalConnectionsJudiCare[0].link.id}-judi-care-external-connection`}
      icon="link-out"
      iconPosition="right"
      appearance="secondary"
      onClick={() => {
        onExternalConnection(externalConnectionsJudiCare[0].link);
      }}
    >
      {translate(`${TRANSLATION_PATH}.providerSearch`)}
    </ThemedButton>
  );

  return (
    <LoadingGroup loading={isLoading}>
      <LoadingGroup.Placeholder
        fallback={
          <Shelf>
            <Shelf.Spacer />
            <Spinner size="large" />
            <Shelf.Spacer />
          </Shelf>
        }
      >
        <Panel appearance="primary">
          <Stack spacing="medium">
            <Shelf alignment="center">
              <LogoImg src={JudiCareLogo} alt="Judi Care Logo" />
              <Shelf.Spacer />
            </Shelf>
            <Body>
              {translate(`${TRANSLATION_PATH}.aboutJudiCare`)}
            </Body>
            {isMobile && (
              <Grid>
                <Grid.Spread columns={12}>
                  {button}
                </Grid.Spread>
              </Grid>
            )}
            {!isMobile && (
              <Shelf>
                {button}
                <Shelf.Spacer />
              </Shelf>
            )}
          </Stack>
        </Panel>
      </LoadingGroup.Placeholder>
    </LoadingGroup>
  );
};
